import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import FormikControl from '../components/FormikContainer/FormikControl';
import UsernameInput from '../components/UsernameInput';
import PrimaryButton from '../components/PrimaryButton';
import StudentHeroSegment from '../components/StudentHeroSegment';
import { AppDispatch, RootState } from '../ApplicationState';
import { updateUserProfile } from '../BackendInterface';
import { isUserHasKey, scrollIntoViewHelper, validateEmailInput } from '../GlobalFunctions';
import { SpinnerOverlay } from '../SpinnerOverlay';
import { updateAllFields, UpdateUserProfile } from '../UserManagement';
import { Icon } from '@iconify/react';
import chevronLeft from '@iconify/icons-akar-icons/chevron-left';
import styles from './MiddleSchoolAccountSettings.module.scss';

const mapStateToProps = (state: RootState) => ({
  userProfile: state.user.userProfile,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateUserProfile: (partialUserProfile: UpdateUserProfile) => dispatch(updateAllFields(partialUserProfile)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Pronouns = [
  { key: 'Select pronouns', value: 'placeholder' },
  { key: 'He/Him', value: 'He/Him' },
  { key: 'She/Her', value: 'She/Her' },
  { key: 'They/Them', value: 'They/Them' },
];

interface ProfileFormValues {
  firstName: string;
  lastName: string;
  email: string;
  pronouns: string;
  schoolName: string;
  grade: string;
}

const AccoutSettingsProfileSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  schoolName: Yup.string().required('Required'),
  grade: Yup.number().min(6, 'Please enter grade 6 - 8').max(8, 'Please enter grade 6 - 8').required('Required'),
});

const MiddleSchoolAccountSettings = (props: PropsFromRedux) => {
  const [isShowSpinner, setShowSpinner] = useState<boolean>(false);
  const [isShowSuccess, setShowSuccess] = useState<boolean>(false);
  const [isShowError, setShowError] = useState<boolean>(false);
  const [apiResponse, setApiResponse] = useState<string>('');

  const history = useHistory();

  if (!props.userProfile) {
    return null;
  }

  const { firstName, lastName, email, pronouns, schoolName, grade } = props.userProfile;

  const isGradeLevelLocked = !isUserHasKey('KEY_CHANGE_GRADE_LEVEL');

  const initialProfileValues: ProfileFormValues = {
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    email: email ?? '',
    pronouns: pronouns ?? '',
    schoolName: schoolName ?? '',
    grade: isGradeLevelLocked ? 'none' : (grade?.toString() ?? ''),
  };

  const handleSubmit = async (values: ProfileFormValues, { setSubmitting }: FormikHelpers<ProfileFormValues>) => {
    if (!props.userProfile) {
      return;
    }

    setShowSpinner(true);
    const requestData = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      pronouns: values.pronouns,
      schoolName: values.schoolName,
      grade: parseInt(values.grade, 10),
    };

    try {
      await updateUserProfile({ newUserData: requestData });
      props.updateUserProfile({ ...props.userProfile, ...requestData });
      setShowSpinner(false);
      setShowSuccess(true);
    } catch (error) {
      if (error instanceof Error) {
        setApiResponse(error.message);
      } else {
        setApiResponse('Unknown error');
      }
      setShowSpinner(false);
      setShowError(true);
    }
    setSubmitting(false);
  };

  return (
    <>
      <SpinnerOverlay isShow={isShowSpinner} />
      <StudentHeroSegment home backgroundImage={''}>
        <div className={styles.heroContent}>
          <button className={styles.goBackButton} onClick={() => history.push('/studentPortal/toDos')}>
            <Icon icon={chevronLeft} className={styles.chevronLeft} />
          </button>
          <h1>Student Account Settings</h1>
        </div>
      </StudentHeroSegment>
      <div className={styles.formContainer}>
        <Formik
          initialValues={initialProfileValues}
          onSubmit={handleSubmit}
          validationSchema={AccoutSettingsProfileSchema}
        >
          {({ errors }) => {
            return (
              <Form className={styles.formikForm}>
                <div className={styles.personalInfoContainer}>
                  <h1>Personal Information</h1>
                  <div className={styles.inputs}>
                    <FormikControl control='input' type='text' label='First Name' name='firstName' />
                    <FormikControl control='input' type='text' label='Last Name' name='lastName' />
                    <UsernameInput label='Email Address' name='email' type='text' validate={validateEmailInput} />
                    <FormikControl control='select' label='Pronouns' name='pronouns' options={Pronouns} />
                  </div>
                </div>
                <div className={styles.schoolInfoContainer}>
                  <h1>School Information</h1>
                  <div className={styles.inputs}>
                    <FormikControl control='input' type='text' label='School Name' name='schoolName' />
                    <FormikControl control='input' type='text' label='Grade' name='grade' />
                  </div>
                </div>
                <div className={styles.buttonRow}>
                  <PrimaryButton small type='submit' onClick={() => scrollIntoViewHelper(errors)}>
                    save changes
                  </PrimaryButton>
                  <div
                    className={isShowSuccess ? styles.successShown : styles.successHidden}
                    onTransitionEnd={() => setShowSuccess(false)}
                  >
                    Nice! Your account has been successfully updated.
                  </div>
                  <div
                    className={isShowError ? styles.errorShown : styles.errorHidden}
                    onTransitionEnd={() => setShowError(false)}
                  >
                    {apiResponse}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default connector(MiddleSchoolAccountSettings);
